// Centralized auth-aware fetch wrapper + admin auth helpers.
// Mirrors console-ui's utils.ts, trimmed to what the admin console needs.

export const ADMIN_ROLE = 'ROLE_ADMIN';

export function getBearerToken(): string | null {
  const tokenString = localStorage.getItem('token');
  if (tokenString != null && tokenString !== 'undefined') {
    return tokenString;
  }
  return null;
}

/**
 * Normalize the stored roles into a flat string[].
 * The backend may return roles as a JSON array or as comma-joined strings,
 * so we defensively split on commas (same approach as console-ui).
 */
export function rolesFromValue(value: unknown): string[] {
  if (!value) return [];
  let parsed = value;
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
    } catch {
      parsed = [value];
    }
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.flatMap(r => String(r).split(',')).map(r => r.trim()).filter(Boolean);
}

export function getRoles(): string[] {
  return rolesFromValue(localStorage.getItem('roles'));
}

export function rolesIncludeAdmin(value: unknown): boolean {
  return rolesFromValue(value).includes(ADMIN_ROLE);
}

/** True if the currently stored session has ROLE_ADMIN. */
export function isAdmin(): boolean {
  return getRoles().includes(ADMIN_ROLE);
}

// --- Centralized auth-aware fetch wrapper ---

let _loggingOut = false;

/**
 * fetch wrapper that:
 * 1. Prepends the API base URL
 * 2. Injects the Authorization header
 * 3. Detects 401 responses and triggers a single logout
 * 4. Short-circuits all calls once a logout is in progress
 */
export async function apiFetch(path: string, options: RequestInit = {}): Promise<Response> {
  if (_loggingOut) {
    return new Response(null, { status: 401, statusText: 'Logging out' });
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: 'Bearer ' + getBearerToken(),
    ...(options.headers || {})
  };

  const response = await fetch(process.env['API_URL'] + path, { ...options, headers });

  if (response.status === 401 && !_loggingOut) {
    _loggingOut = true;
    logout();
    return response;
  }

  return response;
}

export const logout = () => {
  _loggingOut = true;
  localStorage.clear();
  sessionStorage.clear();
  window.location.reload();
};

export function parseResponse(response: Response) {
  if (response.ok) {
    return response.json();
  }
  return undefined;
}

// --- Auth ---

export interface LoginResponse {
  accessToken: string;
  tokenType?: string;
  roles?: string[] | string;
  displayName?: string;
  email?: string;
}

/**
 * Login does NOT use apiFetch — no auth header needed, and a 401 means bad
 * credentials rather than an expired token.
 */
export async function loginUser(credentials: { email: string; password: string }): Promise<LoginResponse> {
  const response = await fetch(process.env['API_URL'] + '/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(credentials)
  });

  if (response.ok) {
    return response.json();
  }
  const error: any = new Error(response.status === 401 ? 'Invalid email or password' : 'Login failed');
  error.status = response.status;
  throw error;
}

export async function getUserInfo() {
  return apiFetch('/users/me').then(parseResponse);
}
